import { addMonths, endOfQuarter, format, parseISO } from 'date-fns'
import { isNumber } from 'lodash'
import DolfinCurrency, { ExchangeInfo as CurrencyExchangeInfo } from '../currency'
import { Enums, TablesInsert } from '../db_types'
import { DolfinDiagram } from '../diagram'
import { DolfinExecution } from '../execution'
import DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { NodeError, NodeWarning, PaymentNodeOutput, PaymentNodeOutputElement } from '../types'
import { DolfinNode } from './base'
import { PaymentFormData, TypedNode } from './types'

/**
 * Payment node - persists payouts generated by compensation logic.
 *
 * Handles:
 * - Payment phases (single, multi-phase splits)
 * - Installments
 * - Payment timing (current month, next month, custom, conditional)
 * - Withholding logic
 *
 * @see /docs/domain/nodes/payment.md for canonical documentation
 */

// Types for better parameter handling
type ExchangeInfo = {
  currency: string
  originalValue: number
  exchangeRate: number
  converted_at: string
}
type ConversionResult = number | CurrencyExchangeInfo | null

type PayoutInstallment = Omit<TablesInsert<'payout_installments'>, 'payout_id'> & {
  payment_date?: string
}

type PayoutParams = {
  receiver: string
  accrualDate: string
  timing: string
  value: number
  withheld: boolean
  phaseId: string
  paymentDate?: string
  compPeriod?: { start: string; end: string }
  exchangeInfo?: ExchangeInfo
  payoutInstallments?: PayoutInstallment[]
}

type Phase = PaymentFormData['phases'][number]
type InstallmentsConfig = NonNullable<PaymentFormData['installments']>
type WithheldPhase = Pick<Phase, 'timing' | 'date_field' | 'filters' | 'isInstallments'>
type ProfileCompensationValue = { profile_id: string; value: number }
type ExchangeConfigTableName = Extract<
  Enums<'customizable_tables'>,
  'deals' | 'activities' | 'invoices' | 'kpis' | 'line_items' | 'custom_records' | 'deal_line_items'
>

const SOURCE_TO_EXCHANGE_CONFIG_TABLE: Record<Enums<'plan_source'>, ExchangeConfigTableName> = {
  deal: 'deals',
  activity: 'activities',
  invoice: 'invoices',
  kpi: 'kpis',
  line_item: 'line_items',
  custom_record: 'custom_records',
  deal_line_item: 'deal_line_items'
}

export class PaymentNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'payment'> {
  private static readonly exchangeConfigByOrgAndTable: Map<string, Map<number | null, string>> =
    new Map()
  private static readonly exchangeConfigByOrgAndTableInFlight: Map<
    string,
    Promise<Map<number | null, string>>
  > = new Map()

  private _accrualDate: string | null = null
  private currencyHelper = new DolfinCurrency(
    this.flowExecutor.organizationId,
    this.flowExecutor.rpcClient
  )

  getAccrualDate(): string {
    if (!this._accrualDate) {
      this._accrualDate = this.flowExecutor.compensationRule.outcome_period
        ? this.flowExecutor.period!.end
        : this.flowExecutor.recordData!.getProcessingDate(
            this.flowExecutor.compensationRule.accrual_date_field,
            this.flowExecutor.customFields
          )
    }
    return this._accrualDate!
  }

  getPaymentDate(phase: Phase, withheld: boolean): string | null {
    if (
      !this.flowExecutor.compensationRule.outcome_period &&
      (phase.timing === 'custom' || phase.timing === 'conditional') &&
      !withheld
    ) {
      return this.flowExecutor.recordData!.getProcessingDate(
        phase.date_field,
        this.flowExecutor.customFields
      )
    }

    switch (phase.timing) {
      case 'current_month':
        return this.getAccrualDate()
      case 'next_month':
        return format(addMonths(parseISO(this.getAccrualDate()), 1), 'yyyy-MM-dd')
      case 'end_of_quarter':
        return format(endOfQuarter(parseISO(this.getAccrualDate())), 'yyyy-MM-dd')
      default:
        return null
    }
  }

  isWithheld(phase: WithheldPhase): boolean {
    const paymentDate = phase.date_field
      ? this.flowExecutor.recordData!.getProcessingDate(
          phase.date_field,
          this.flowExecutor.customFields
        )
      : null

    return (
      (phase.timing === 'custom' && !paymentDate) ||
      (phase.timing === 'conditional' &&
        !!phase.filters &&
        (!phase.date_field ||
          (!paymentDate && !phase.isInstallments) ||
          !this.evaluateLogic(phase.filters as Record<string, unknown>)))
    )
  }

  private isInformative(value: number): boolean {
    return (
      value < 0 &&
      !!this.flowExecutor.compensationRule.long_term_balancing_period &&
      this.flowExecutor.compensationRule.long_term_balancing_clawback_method === 'informative'
    )
  }

  private async getFinalPaymentAmount(value: number, accrualDate: string) {
    const originalCurrency = this.flowExecutor.compensationRule.outcome_period
      ? await this.currencyHelper.getTeamCurrency(this.flowExecutor.profileId!, accrualDate)
      : this.flowExecutor.recordData!.getCurrency()

    const needsToBeConverted =
      originalCurrency && (await this.currencyHelper.shouldBeConverted(originalCurrency))

    if (needsToBeConverted) {
      const exchangeRateDate = await this.getExchangeRateDate(accrualDate)
      return this.currencyHelper.convert(
        value,
        originalCurrency,
        undefined,
        exchangeRateDate,
        this.flowExecutor.recordData
      )
    } else {
      return value
    }
  }

  private getExchangeConfigCacheKey(
    organizationId: number,
    tableName: ExchangeConfigTableName
  ): string {
    return `${organizationId}-${tableName}`
  }

  private async getExchangeConfigBySourceTableId(
    organizationId: number,
    tableName: ExchangeConfigTableName
  ): Promise<Map<number | null, string>> {
    const cacheKey = this.getExchangeConfigCacheKey(organizationId, tableName)
    const cached = PaymentNode.exchangeConfigByOrgAndTable.get(cacheKey)
    if (cached) {
      return cached
    }

    const inFlight = PaymentNode.exchangeConfigByOrgAndTableInFlight.get(cacheKey)
    if (inFlight) {
      return inFlight
    }

    const loadPromise = (async () => {
      const configs = await this.flowExecutor.rpcClient.getExchangeConfigs(
        organizationId,
        tableName
      )
      const configBySourceTableId = new Map<number | null, string>()

      for (const config of configs) {
        configBySourceTableId.set(
          tableName === 'kpis'
            ? config.kpis_table_id
            : tableName === 'custom_records'
              ? config.custom_record_table_id
              : null,
          config.reference_date_field
        )
      }

      PaymentNode.exchangeConfigByOrgAndTable.set(cacheKey, configBySourceTableId)
      return configBySourceTableId
    })()

    PaymentNode.exchangeConfigByOrgAndTableInFlight.set(cacheKey, loadPromise)

    try {
      return await loadPromise
    } finally {
      PaymentNode.exchangeConfigByOrgAndTableInFlight.delete(cacheKey)
    }
  }

  private async getConfiguredExchangeReferenceDateField(): Promise<string | null> {
    if (this.flowExecutor.compensationRule.outcome_period) {
      return null
    }

    const source = this.flowExecutor.compensationRule.source
    const tableName = SOURCE_TO_EXCHANGE_CONFIG_TABLE[source]
    const configsBySourceTableId = await this.getExchangeConfigBySourceTableId(
      this.flowExecutor.organizationId,
      tableName
    )

    if (source === 'kpi') {
      return configsBySourceTableId.get(this.flowExecutor.compensationRule.kpis_table_id) ?? null
    }

    if (source === 'custom_record') {
      return (
        configsBySourceTableId.get(this.flowExecutor.compensationRule.custom_record_table_id) ??
        null
      )
    }

    return configsBySourceTableId.get(null) ?? null
  }

  private async getExchangeRateDate(accrualDate: string): Promise<string> {
    if (this.flowExecutor.compensationRule.outcome_period || !this.flowExecutor.recordData) {
      return accrualDate
    }

    const configuredDateField = await this.getConfiguredExchangeReferenceDateField()
    if (configuredDateField) {
      const configuredDate = this.flowExecutor.recordData.getProcessingDate(
        configuredDateField,
        this.flowExecutor.customFields
      )

      if (configuredDate) {
        return configuredDate
      }

      this.logger.warn(
        `Configured exchange rate reference field "${configuredDateField}" has no date value for source "${this.flowExecutor.compensationRule.source}"`
      )
    }

    return (
      this.flowExecutor.recordData.getProcessingDate(undefined, this.flowExecutor.customFields) ??
      accrualDate
    )
  }

  private createExchangeInfo(paymentValueInfo: ConversionResult): ExchangeInfo | undefined {
    if (paymentValueInfo && !isNumber(paymentValueInfo)) {
      return {
        currency: paymentValueInfo.query.from,
        originalValue: paymentValueInfo.query.amount,
        exchangeRate: paymentValueInfo.info.rate,
        converted_at: paymentValueInfo.date
      }
    }
    return undefined
  }

  private async processPaymentValueInfo(
    compensationValue: number,
    accrualDate: string,
    profileId: string
  ): Promise<{
    paymentValueInfo: ConversionResult
    compValue: number
    exchangeInfo: ExchangeInfo | undefined
  }> {
    this.assertFinitePaymentValue(compensationValue, profileId, 'compensation')
    const paymentValueInfo =
      (await this.getFinalPaymentAmount(compensationValue, accrualDate)) ?? compensationValue
    const compValue = isNumber(paymentValueInfo) ? paymentValueInfo : paymentValueInfo.result
    this.assertFinitePaymentValue(compValue, profileId, 'converted payment')

    this.logger.info(`Payment value info: ${JSON.stringify(paymentValueInfo)}`)
    return {
      paymentValueInfo,
      compValue,
      exchangeInfo: this.createExchangeInfo(paymentValueInfo)
    }
  }

  private assertFinitePaymentValue(value: number, profileId: string, stage: string): void {
    if (!Number.isFinite(value)) {
      const period = this.flowExecutor.period
      throw new Error(
        `Invalid ${stage} value ${String(value)} for rule ${this.flowExecutor.compensationRule.id}, ` +
          `payment node ${this.node.id}, profile ${profileId}, ` +
          (period
            ? `period ${period.start} to ${period.end}`
            : `accrual date ${this.getAccrualDate()}`)
      )
    }
  }

  private async processInstallments(
    compensationValue: { profile_id: string; value: number },
    compPeriod?: { start: string; end: string }
  ): Promise<PaymentNodeOutputElement> {
    const installmentsConfig = this.formData.installments as InstallmentsConfig
    const withheld =
      !!installmentsConfig.conditions &&
      this.isWithheld({
        timing: 'conditional',
        filters: installmentsConfig.conditions,
        isInstallments: true
      })

    const { compValue, exchangeInfo } = await this.processPaymentValueInfo(
      compensationValue.value,
      this.getAccrualDate(),
      compensationValue.profile_id
    )

    const installments = this.getInstallments(
      installmentsConfig.firstPayment,
      installmentsConfig.numberOfPayments,
      installmentsConfig.paymentFrequency,
      compValue,
      exchangeInfo ? compensationValue.value : undefined
    )

    return this.buildPayout({
      receiver: compensationValue.profile_id,
      accrualDate: this.getAccrualDate(),
      timing: installmentsConfig.firstPayment,
      value: compValue,
      withheld: this.isInformative(compensationValue.value) || withheld,
      phaseId: '',
      paymentDate: undefined,
      compPeriod,
      exchangeInfo,
      payoutInstallments: installments
    })
  }

  private async processSinglePhase(
    compensationValue: { profile_id: string; value: number },
    phase: Phase,
    compPeriod?: { start: string; end: string }
  ): Promise<PaymentNodeOutputElement> {
    const withheld = this.isWithheld(phase)
    const paymentDate = this.getPaymentDate(phase, withheld)

    const { compValue, exchangeInfo } = await this.processPaymentValueInfo(
      compensationValue.value,
      this.getAccrualDate(),
      compensationValue.profile_id
    )

    return this.buildPayout({
      receiver: compensationValue.profile_id,
      accrualDate: this.getAccrualDate(),
      timing: phase.timing,
      value: compValue,
      withheld: this.isInformative(compensationValue.value) || withheld,
      phaseId: phase.id || '',
      paymentDate: paymentDate ?? undefined,
      compPeriod,
      exchangeInfo
    })
  }

  private async processMultiplePhases(
    compensationValue: { profile_id: string; value: number },
    phases: Phase[],
    withheld = false,
    compPeriod?: { start: string; end: string }
  ): Promise<PaymentNodeOutputElement> {
    const { compValue, exchangeInfo } = await this.processPaymentValueInfo(
      compensationValue.value,
      this.getAccrualDate(),
      compensationValue.profile_id
    )

    const payoutInstallments: PayoutInstallment[] = []

    for (const phase of phases) {
      const phaseWithheld = this.isWithheld(phase)
      const paymentDate = this.getPaymentDate(phase, phaseWithheld)

      payoutInstallments.push({
        amount: compValue * (phase.percentage ?? 1),
        payment_date: paymentDate ?? undefined,
        phase_id: phase.id,
        original_amount: exchangeInfo
          ? compensationValue.value * (phase.percentage ?? 1)
          : undefined
      })
    }

    return this.buildPayout({
      receiver: compensationValue.profile_id,
      accrualDate: this.getAccrualDate(),
      timing: '',
      value: compValue,
      withheld,
      phaseId: '',
      compPeriod,
      exchangeInfo,
      payoutInstallments
    })
  }

  private getPreviousPayoutsByProfile(
    compensationValues: ProfileCompensationValue[]
  ): Map<string, number> {
    const previousPayouts =
      this.flowExecutor.execution.getVariable<unknown>('total_previous_payouts')
    const previousPayoutsByProfile = new Map<string, number>()

    if (typeof previousPayouts === 'number') {
      for (const compensationValue of compensationValues) {
        previousPayoutsByProfile.set(compensationValue.profile_id, previousPayouts)
      }

      return previousPayoutsByProfile
    }

    if (!Array.isArray(previousPayouts)) {
      return previousPayoutsByProfile
    }

    for (const payout of previousPayouts) {
      const profileId =
        payout &&
        typeof payout === 'object' &&
        'profile_id' in payout &&
        typeof payout.profile_id === 'string'
          ? payout.profile_id
          : undefined
      const value =
        payout &&
        typeof payout === 'object' &&
        'value' in payout &&
        typeof payout.value === 'number'
          ? payout.value
          : undefined

      if (!profileId || value === undefined) {
        continue
      }

      previousPayoutsByProfile.set(
        profileId,
        (previousPayoutsByProfile.get(profileId) ?? 0) + value
      )
    }

    return previousPayoutsByProfile
  }

  private getPeriodCandidates(compensationValues: ProfileCompensationValue[]): {
    periodCandidatesByProfile: Map<string, number>
    scalarPeriodCandidate?: number
  } {
    const periodCandidatesByProfile = new Map<string, number>()
    const periodCandidates =
      this.node.data.parsedValues?.compensationValuesPeriodCandidates ??
      this.flowExecutor.execution.getVariable<ProfileCompensationValue[]>(
        'compensation_values_period_candidates'
      )
    const scalarPeriodCandidate =
      this.node.data.parsedValues?.compensationValuePeriodCandidate ??
      this.flowExecutor.execution.getVariable<number>('compensation_value_period_candidate')

    if (periodCandidates) {
      for (const periodCandidate of periodCandidates) {
        periodCandidatesByProfile.set(periodCandidate.profile_id, periodCandidate.value)
      }
    } else if (scalarPeriodCandidate !== undefined) {
      for (const compensationValue of compensationValues) {
        periodCandidatesByProfile.set(compensationValue.profile_id, scalarPeriodCandidate)
      }
    }

    return {
      periodCandidatesByProfile,
      scalarPeriodCandidate
    }
  }

  private applyFinalLongTermBalancing(
    compensationValue: ProfileCompensationValue,
    previousPayoutsByProfile: Map<string, number>,
    periodCandidatesByProfile: Map<string, number>,
    scalarPeriodCandidate?: number
  ): ProfileCompensationValue {
    if (!this.hasLongTermBalancing()) {
      return compensationValue
    }

    const ltbValue =
      compensationValue.value - (previousPayoutsByProfile.get(compensationValue.profile_id) ?? 0)

    if (!this.shouldApplyLongTermOnlyIfHigher()) {
      return {
        ...compensationValue,
        value: ltbValue
      }
    }

    const periodCandidate =
      periodCandidatesByProfile.get(compensationValue.profile_id) ?? scalarPeriodCandidate

    return {
      ...compensationValue,
      value: periodCandidate === undefined ? ltbValue : Math.max(ltbValue, periodCandidate)
    }
  }

  async process(): Promise<PaymentNodeOutput> {
    if (!this.flowExecutor.compensationRule.outcome_period && !this.getAccrualDate()) {
      const dateField = this.flowExecutor.compensationRule.accrual_date_field
      console.warn('Processing date not found: ', dateField)
      return []
    }

    const compPeriod = this.flowExecutor.compensationRule.outcome_period
      ? this.flowExecutor.period
      : undefined

    const result: PaymentNodeOutput = []
    const compensationValues = await this.getCompensationValues()
    const previousPayoutsByProfile = this.getPreviousPayoutsByProfile(compensationValues)
    const { periodCandidatesByProfile, scalarPeriodCandidate } =
      this.getPeriodCandidates(compensationValues)

    for (const compensationValue of compensationValues) {
      const finalCompensationValue = this.applyFinalLongTermBalancing(
        compensationValue,
        previousPayoutsByProfile,
        periodCandidatesByProfile,
        scalarPeriodCandidate
      )

      if (this.formData.paymentType === 'installments') {
        result.push(await this.processInstallments(finalCompensationValue, compPeriod))
      } else if (this.formData.phases.length === 1) {
        const phase = this.formData.phases[0]
        result.push(await this.processSinglePhase(finalCompensationValue, phase, compPeriod))
      } else {
        const informativeWithheld = this.isInformative(finalCompensationValue.value)

        result.push(
          await this.processMultiplePhases(
            finalCompensationValue,
            this.formData.phases,
            informativeWithheld,
            compPeriod
          )
        )
      }
    }

    return result
  }

  private async getCompensationValues() {
    const compensationValues = this.node.data.parsedValues?.compensationValues ?? []

    if (
      compensationValues.length === 0 &&
      this.node.data.parsedValues!.compensationValue !== undefined
    ) {
      const recipient = this.formData.recipients[0].recipient
      const receiver = await this.getOutcomeReceiver(recipient)

      if (!!receiver) {
        compensationValues.push({
          profile_id: receiver,
          value: this.node.data.parsedValues!.compensationValue
        })
      } else {
        console.warn('Recipient not found:', recipient)
      }
    }

    return compensationValues
  }

  async parse(_: DolfinDiagram, execution?: DolfinExecution) {
    this.parseCompensationValues(execution)

    return this.node
  }

  static validate(
    node: TypedNode<'payment'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }

  private async getOutcomeReceiver(recipientType: string): Promise<string | undefined | null> {
    // Team manager recipient
    if (recipientType === 'team_manager') {
      const profileId =
        this.flowExecutor.profileId ??
        (this.flowExecutor.recordData!.getFieldValue(
          this.flowExecutor.compensationRule.user_field,
          this.flowExecutor.customFields
        ) as string)

      const organizationId =
        this.flowExecutor.organizationId ?? this.flowExecutor.recordData!.getOrganizationId()

      return this.flowExecutor.rpcClient.getManagerId(profileId, organizationId)
    }

    // Owner recipient
    if (recipientType === 'owner') {
      return this.flowExecutor.compensationRule.outcome_period
        ? this.flowExecutor.profileId
        : this.flowExecutor.recordData?.getDealOwnerId()
    }

    // Other recipient types (when not outcome period)
    if (!this.flowExecutor.compensationRule.outcome_period) {
      return (this.flowExecutor.recordData?.getFieldValue(
        recipientType,
        this.flowExecutor.customFields
      ) || this.getDefaultValueForField(recipientType)) as string
    }

    return undefined
  }

  private getDefaultValueForField(fieldName: string): string | undefined {
    const normalizedFieldName = fieldName
      .replace(/^(deal|invoice)\.\$/, (_, p1) => `${p1}.`)
      .replace(/^\$/, '')

    const defaultValue = this.flowExecutor.customFields?.[normalizedFieldName]?.default_value
    return typeof defaultValue === 'string' ? defaultValue : undefined
  }

  private buildPayout(params: PayoutParams): PaymentNodeOutputElement {
    const {
      receiver,
      accrualDate,
      timing,
      value,
      withheld,
      phaseId,
      paymentDate,
      compPeriod,
      exchangeInfo,
      payoutInstallments = []
    } = params

    return {
      organization_id: this.flowExecutor.organizationId,
      type: this.flowExecutor.compensationRule.type,
      value,
      profile_id: receiver,
      period_start: compPeriod?.start,
      period_end: compPeriod?.end,
      plan_id: this.flowExecutor.compensationRule.plan_id,
      compensation_rule_id: this.flowExecutor.compensationRule.id,
      deal_id: this.getValueIfRecordRule(() => this.flowExecutor.recordData!.getDealId()),
      kpi_id: this.getValueIfRecordRule(() => this.flowExecutor.recordData!.getKpiId()),
      activity_id: this.getValueIfRecordRule(() => this.flowExecutor.recordData!.getActivityId()),
      invoice_id: this.getValueIfRecordRule(() => this.flowExecutor.recordData!.getInvoiceId()),
      line_item_id: this.getValueIfRecordRule(() => this.flowExecutor.recordData!.getLineItemId()),
      deal_line_item_id: this.getValueIfRecordRule(() =>
        this.flowExecutor.recordData!.getDealLineItemId()
      ),
      custom_record_id: this.getValueIfRecordRule(() =>
        this.flowExecutor.recordData!.getCustomRecordId()
      ),
      effective_date: this.flowExecutor.compensationRule.outcome_period
        ? compPeriod?.end
        : accrualDate,
      description: this.flowExecutor.compensationRule.label,
      compensation: this.flowExecutor.compensationRule.type,
      timing,
      withheld,
      payment_date: paymentDate,
      phase_id: phaseId,
      currency: exchangeInfo?.currency,
      original_value: exchangeInfo?.originalValue,
      exchange_rate: exchangeInfo?.exchangeRate,
      converted_at: exchangeInfo?.converted_at,
      payout_installments: payoutInstallments
    }
  }

  private getValueIfRecordRule<T>(valueGetter: () => T): T | undefined {
    return !this.flowExecutor.compensationRule.outcome_period ? valueGetter() : undefined
  }

  private getInstallments(
    firstPayment: string,
    numberOfPayments: number,
    paymentFrequency: string,
    compValue: number,
    originalValue?: number
  ): PayoutInstallment[] {
    const firstPaymentDate = this.calculateFirstPaymentDate(firstPayment)
    const payoutInstallments: PayoutInstallment[] = []

    for (let i = 0; i < numberOfPayments; i++) {
      const installmentPaymentDate = this.calculateInstallmentDate(
        firstPaymentDate,
        i,
        paymentFrequency
      )

      payoutInstallments.push({
        amount: compValue / numberOfPayments,
        installment_number: i,
        payment_date: installmentPaymentDate,
        original_amount: originalValue ? originalValue / numberOfPayments : undefined
      })
    }

    return payoutInstallments
  }

  private calculateFirstPaymentDate(firstPayment: string): string {
    return firstPayment === 'next_period'
      ? format(addMonths(parseISO(this.getAccrualDate()), 1), 'yyyy-MM-dd')
      : this.getAccrualDate()
  }

  private calculateInstallmentDate(
    firstPaymentDate: string,
    installmentNumber: number,
    paymentFrequency: string
  ): string {
    if (installmentNumber === 0) {
      return firstPaymentDate
    }

    const monthsToAdd = this.getMonthsToAdd(paymentFrequency) * installmentNumber
    return format(addMonths(parseISO(firstPaymentDate), monthsToAdd), 'yyyy-MM-dd')
  }

  private getMonthsToAdd(paymentFrequency: string): number {
    switch (paymentFrequency) {
      case 'year':
        return 12
      case 'half_year':
        return 6
      case 'quarter':
        return 3
      default:
        return 1
    }
  }
}
